import React, { useState, useMemo } from 'react';
import { reportsApi } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { MultiSelect } from './MultiSelect';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Loader2, Search, Download, FileCheck, ChevronRight, Info, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

// "Registration Invoiced Report — there will be a start date filter, the result
// will be Branch, Class. Note: 3rd sibling is exempt of registration fee." (2026-09-03)
//
// The start date picks the ADMISSION cohort (Date of Joining) — the same window
// the New Admission Report uses — and the report then asks who in that cohort
// actually carries a registration fee line. Four buckets that always add to
// Admitted, so the row reads as a reconciliation rather than a bare count.
//
// Sibling rank is DERIVED (parent email + Date of Joining), because nothing in the
// system records a child's position in the family. A student with no parent email
// is never called exempt — that would silently excuse a genuine miss — so they get
// their own Unknown column to be looked at by hand.

const num = (n) => Number(n || 0).toLocaleString();
const usd = (n) => `USD ${Number(n || 0).toLocaleString(undefined,
  { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const CATEGORY_LABEL = {
  invoiced: 'Invoiced',
  exempt: 'Exempt (3rd+ sibling)',
  unknown: 'Unknown (no parent email)',
  missing: 'Missing',
};

const toCsv = (rows, columns) => {
  const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  return [columns.map((c) => esc(c.label)).join(','),
    ...rows.map((r) => columns.map((c) => esc(c.get(r))).join(','))].join('\n');
};

const downloadBlob = (name, payload, type) => {
  const blob = payload instanceof Blob ? payload : new Blob([payload], { type });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', name);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

const STUDENT_COLUMNS = [
  { label: 'Student ID', get: (r) => r.student_id },
  { label: 'Student', get: (r) => r.name },
  { label: 'Branch', get: (r) => r.branch },
  { label: 'Class', get: (r) => r.current_class },
  { label: 'Date of Joining', get: (r) => r.date_of_joining },
  { label: 'Joining Source', get: (r) => r.joining_source },
  { label: 'Registration', get: (r) => CATEGORY_LABEL[r.category] || r.category },
  { label: 'Amount (USD)', get: (r) => r.registration_amount || '' },
  { label: 'Invoice #', get: (r) => r.invoice_numbers },
  { label: 'Sibling Rank', get: (r) => r.sibling_rank },
  { label: 'Siblings In School', get: (r) => r.siblings_in_school },
  { label: 'Parent/Guardian', get: (r) => r.parent_guardian },
  { label: 'Parent Email', get: (r) => r.parent_email },
];

const RegistrationInvoicedReport = ({ configBranches = [], configClasses = [] }) => {
  const { user, isAdmin, userBranches } = useAuth();
  const branchOptions = isAdmin ? configBranches : (userBranches || []);
  const canExport = ['admin', 'manager'].includes(user?.role);

  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [branches, setBranches] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [applied, setApplied] = useState(null);
  const [exporting, setExporting] = useState(false);

  const [drill, setDrill] = useState(null);
  const [drillData, setDrillData] = useState(null);
  const [drillLoading, setDrillLoading] = useState(false);
  const [drillSearch, setDrillSearch] = useState('');

  const buildParams = () => ({
    ...(startDate ? { start_date: startDate } : {}),
    ...(endDate ? { end_date: endDate } : {}),
    ...(branches.length ? { branch: branches.join(',') } : {}),
    ...(classes.length ? { current_class: classes.join(',') } : {}),
  });

  const submit = async () => {
    if (!startDate) {
      toast.error('Pick a start date first');
      return;
    }
    if (endDate && endDate < startDate) {
      toast.error('End date is before the start date');
      return;
    }
    setLoading(true);
    const params = buildParams();
    try {
      const res = await reportsApi.registrationInvoiced(params);
      setData(res.data);
      setApplied(params);
      setDrill(null);
      setDrillData(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load registration invoiced report');
    } finally {
      setLoading(false);
    }
  };

  const exportExcel = async () => {
    if (!applied) return;
    setExporting(true);
    try {
      const res = await reportsApi.exportRegistrationInvoicedExcel(applied);
      const tag = [applied.start_date, applied.end_date].filter(Boolean).join('_to_');
      downloadBlob(`registration_invoiced_${tag}.xlsx`, res.data);
      toast.success('Excel exported');
    } catch (err) {
      toast.error(err?.response?.status === 403
        ? 'Only Admin and Manager can export reports'
        : 'Failed to export Excel');
    } finally {
      setExporting(false);
    }
  };

  // Drill into one Branch x Class cell, optionally narrowed to a single bucket.
  const openDrill = async (cell) => {
    if (!applied) return;
    setDrill(cell);
    setDrillSearch('');
    setDrillData(null);
    setDrillLoading(true);
    try {
      const res = await reportsApi.registrationInvoicedStudents({
        ...applied,
        ...(cell.branch ? { branch: cell.branch } : {}),
        ...(cell.current_class ? { current_class: cell.current_class } : {}),
        ...(cell.category ? { category: cell.category } : {}),
      });
      setDrillData(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load students');
    } finally {
      setDrillLoading(false);
    }
  };

  const filteredDrillRows = useMemo(() => {
    const rows = drillData?.students || [];
    const q = drillSearch.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => [r.name, r.student_id, r.branch, r.current_class,
      r.parent_guardian, r.parent_email, r.invoice_numbers]
      .some((v) => String(v || '').toLowerCase().includes(q)));
  }, [drillData, drillSearch]);

  const rows = useMemo(() => data?.rows || [], [data]);
  const branchTotals = data?.branch_totals || [];
  const totals = data?.totals;
  const totalFor = (b) => branchTotals.find((t) => t.branch === b);

  const lastOfBranch = useMemo(() => {
    const seen = {};
    rows.forEach((r, i) => { seen[r.branch] = i; });
    return seen;
  }, [rows]);

  const rangeLabel = data
    ? `${data.start_date || 'the earliest record'}${data.end_date ? ` to ${data.end_date}` : ' onwards'}`
    : '';

  // A numeric cell that drills when it has something behind it.
  const cell = (row, key, extraClass = '') => (
    <TableCell className={`text-right ${extraClass} ${row[key] ? 'cursor-pointer hover:underline' : ''}`}
      onClick={(e) => { if (row[key]) { e.stopPropagation(); openDrill({ ...row, category: key }); } }}>
      {row[key] ? num(row[key]) : '—'}
    </TableCell>
  );

  return (
    <div className="space-y-4">
      {/* ---- Filters ---- */}
      <Card className="border-[#D8D9E8]">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4 sm:items-end flex-wrap">
            <div className="space-y-1">
              <Label className="text-xs text-[#5C635F] uppercase font-semibold">Start Date</Label>
              <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)}
                className="w-44" data-testid="reginv-start-date" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-[#5C635F] uppercase font-semibold">End Date</Label>
              <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)}
                className="w-44" data-testid="reginv-end-date" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-[#5C635F] uppercase font-semibold">Branch</Label>
              <MultiSelect options={branchOptions} selected={branches} onChange={setBranches}
                placeholder="All Branches" testId="reginv-branch" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-[#5C635F] uppercase font-semibold">Class</Label>
              <MultiSelect options={configClasses} selected={classes} onChange={setClasses}
                placeholder="All Classes" testId="reginv-class" />
            </div>
            <Button onClick={submit} disabled={loading} className="bg-[#3E3F96] hover:bg-[#2D2E7A]"
              data-testid="reginv-submit">
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                : <FileCheck className="w-4 h-4 mr-2" />}
              Submit
            </Button>
            {data && canExport && (
              <Button variant="outline" onClick={exportExcel} disabled={exporting}
                data-testid="reginv-export">
                {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  : <Download className="w-4 h-4 mr-2" />}
                Excel
              </Button>
            )}
          </div>
          <p className="text-xs text-[#5C635F] mt-3">
            The date range picks the <span className="font-medium">admission cohort</span> (Date
            of Joining); the report then checks who in it carries a registration fee line.
          </p>
        </CardContent>
      </Card>

      {!data && !loading && (
        <Card className="border-[#D8D9E8]">
          <CardContent className="flex flex-col items-center justify-center h-48 text-[#5C635F]">
            <FileCheck className="w-12 h-12 mb-4 opacity-50" />
            <p>Pick a start date, then hit Submit.</p>
            <p className="text-xs mt-1">Every number in the table clicks through to the students.</p>
          </CardContent>
        </Card>
      )}

      {data && totals && (
        <>
          {/* ---- KPIs ---- */}
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            <Card className="border-[#3E3F96]/25 bg-[#3E3F96]/10">
              <CardContent className="p-4">
                <p className="text-xs uppercase font-semibold text-[#5C635F]">Admitted</p>
                <p className="text-2xl font-bold text-[#1C1F1D]">{num(totals.admitted)}</p>
                <p className="text-xs text-[#5C635F] mt-1">{rangeLabel}</p>
              </CardContent>
            </Card>
            <Card className="border-[#2E7D6B]/25 bg-[#2E7D6B]/10 cursor-pointer"
              onClick={() => openDrill({ category: 'invoiced' })}>
              <CardContent className="p-4">
                <p className="text-xs uppercase font-semibold text-[#5C635F]">Invoiced</p>
                <p className="text-2xl font-bold text-[#1C1F1D]">{num(totals.invoiced)}</p>
                <p className="text-xs text-[#5C635F] mt-1">{usd(totals.invoiced_amount)}</p>
              </CardContent>
            </Card>
            <Card className="border-[#5556A8]/25 bg-[#5556A8]/10 cursor-pointer"
              onClick={() => openDrill({ category: 'exempt' })}>
              <CardContent className="p-4">
                <p className="text-xs uppercase font-semibold text-[#5C635F]">Exempt</p>
                <p className="text-2xl font-bold text-[#1C1F1D]">{num(totals.exempt)}</p>
                <p className="text-xs text-[#5C635F] mt-1">3rd+ sibling</p>
              </CardContent>
            </Card>
            <Card className="border-[#D99C3B]/25 bg-[#D99C3B]/10 cursor-pointer"
              onClick={() => openDrill({ category: 'unknown' })}>
              <CardContent className="p-4">
                <p className="text-xs uppercase font-semibold text-[#5C635F]">Unknown</p>
                <p className="text-2xl font-bold text-[#1C1F1D]">{num(totals.unknown)}</p>
                <p className="text-xs text-[#5C635F] mt-1">no parent email</p>
              </CardContent>
            </Card>
            <Card className="border-[#C85A40]/25 bg-[#C85A40]/10 cursor-pointer"
              onClick={() => openDrill({ category: 'missing' })}>
              <CardContent className="p-4">
                <p className="text-xs uppercase font-semibold text-[#5C635F]">Missing</p>
                <p className="text-2xl font-bold text-[#1C1F1D]">{num(totals.missing)}</p>
                <p className="text-xs text-[#5C635F] mt-1">should be charged</p>
              </CardContent>
            </Card>
          </div>

          {/* ---- Branch x Class ---- */}
          <Card className="border-[#D8D9E8]">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <FileCheck className="w-4 h-4 text-[#3E3F96]" />
                Registration Fee by Branch and Class
              </CardTitle>
            </CardHeader>
            <CardContent>
              {rows.length === 0 ? (
                <p className="text-sm text-[#5C635F] py-8 text-center">
                  No student joined in this window.
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Branch</TableHead>
                        <TableHead>Class</TableHead>
                        <TableHead className="text-right">Admitted</TableHead>
                        <TableHead className="text-right">Invoiced</TableHead>
                        <TableHead className="text-right">Exempt</TableHead>
                        <TableHead className="text-right">Unknown</TableHead>
                        <TableHead className="text-right">Missing</TableHead>
                        <TableHead className="text-right">Invoiced (USD)</TableHead>
                        <TableHead className="w-8" />
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {rows.map((r, i) => (
                        <React.Fragment key={`${r.branch}-${r.current_class}`}>
                          <TableRow className="hover:bg-[#ECEDF5]/60">
                            <TableCell className="font-medium">
                              {i === 0 || rows[i - 1].branch !== r.branch ? r.branch : ''}
                            </TableCell>
                            <TableCell>{r.current_class}</TableCell>
                            <TableCell className="text-right font-medium cursor-pointer hover:underline"
                              onClick={() => openDrill({ ...r, category: null })}>
                              {num(r.admitted)}
                            </TableCell>
                            {cell(r, 'invoiced', 'text-[#2E7D6B]')}
                            {cell(r, 'exempt', 'text-[#5556A8]')}
                            {cell(r, 'unknown', 'text-[#D99C3B]')}
                            {cell(r, 'missing', 'text-[#C85A40] font-semibold')}
                            <TableCell className="text-right">{usd(r.invoiced_amount)}</TableCell>
                            <TableCell className="text-[#5C635F] cursor-pointer"
                              onClick={() => openDrill({ ...r, category: null })}>
                              <ChevronRight className="w-4 h-4" />
                            </TableCell>
                          </TableRow>
                          {lastOfBranch[r.branch] === i && totalFor(r.branch) && (
                            <TableRow className="bg-[#ECEDF5]/70 font-semibold">
                              <TableCell>{r.branch}</TableCell>
                              <TableCell className="text-[#5C635F] text-sm font-normal">
                                All classes
                              </TableCell>
                              <TableCell className="text-right">{num(totalFor(r.branch).admitted)}</TableCell>
                              <TableCell className="text-right">{num(totalFor(r.branch).invoiced)}</TableCell>
                              <TableCell className="text-right">{num(totalFor(r.branch).exempt)}</TableCell>
                              <TableCell className="text-right">{num(totalFor(r.branch).unknown)}</TableCell>
                              <TableCell className="text-right">{num(totalFor(r.branch).missing)}</TableCell>
                              <TableCell className="text-right">{usd(totalFor(r.branch).invoiced_amount)}</TableCell>
                              <TableCell />
                            </TableRow>
                          )}
                        </React.Fragment>
                      ))}
                      <TableRow className="bg-[#3E3F96]/10 border-t-2 border-[#3E3F96]/30 font-bold">
                        <TableCell>TOTAL</TableCell>
                        <TableCell />
                        <TableCell className="text-right" data-testid="reginv-total">{num(totals.admitted)}</TableCell>
                        <TableCell className="text-right">{num(totals.invoiced)}</TableCell>
                        <TableCell className="text-right">{num(totals.exempt)}</TableCell>
                        <TableCell className="text-right">{num(totals.unknown)}</TableCell>
                        <TableCell className="text-right">{num(totals.missing)}</TableCell>
                        <TableCell className="text-right">{usd(totals.invoiced_amount)}</TableCell>
                        <TableCell />
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>
              )}

              {/* How 'exempt' was decided — this is derived, so it has to be stated. */}
              <div className="text-xs text-[#5C635F] mt-3 space-y-1">
                <p className="flex items-start gap-1">
                  <Info className="w-3 h-3 mt-0.5 shrink-0" />
                  <span>
                    <span className="font-medium">Exempt</span> applies the school rule that the
                    3rd sibling pays no registration fee. Nothing in the system records a child's
                    position in the family, so rank is derived: siblings are grouped on
                    parent email and ordered by Date of Joining.
                  </span>
                </p>
                {totals.missing_on_derived > 0 && (
                  <p className="flex items-start gap-1">
                    <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0 text-[#C85A40]" />
                    <span>
                      <span className="font-medium">{num(totals.missing_on_derived)} of the{' '}
                      {num(totals.missing)} Missing</span> sit on a joining date the
                      2026-08-03 backfill derived from a first invoice, not one keyed by
                      staff — a whole pre-existing branch cohort can look newly admitted this
                      way. Check <span className="font-medium">Joining Source</span> in the
                      drill-down before chasing anyone; only <code>manual</code> is a real
                      admission date.
                    </span>
                  </p>
                )}
                {totals.unknown > 0 && (
                  <p className="flex items-start gap-1">
                    <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0 text-[#D99C3B]" />
                    <span>
                      {num(totals.unknown)} student(s) have no parent email, so their family
                      can't be ranked. They are counted as <span className="font-medium">Unknown</span>,
                      never as exempt — check them by hand.
                    </span>
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {/* ---- Drill-down ---- */}
      <Dialog open={!!drill} onOpenChange={(o) => { if (!o) { setDrill(null); setDrillData(null); } }}>
        <DialogContent className="max-w-6xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 flex-wrap">
              <span>Registration</span>
              {drill?.branch && <Badge variant="outline">{drill.branch}</Badge>}
              {drill?.current_class && <Badge variant="outline">{drill.current_class}</Badge>}
              {drill?.category && <Badge variant="outline">{CATEGORY_LABEL[drill.category]}</Badge>}
              {data && <Badge variant="outline">{rangeLabel}</Badge>}
              {drillData && <Badge className="bg-[#3E3F96]">{num(drillData.count)} students</Badge>}
            </DialogTitle>
          </DialogHeader>

          <div className="flex items-center gap-2 py-2">
            <div className="relative flex-1">
              <Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-[#5C635F]" />
              <Input className="pl-8" placeholder="Search name, ID, class, parent, invoice…"
                value={drillSearch} onChange={(e) => setDrillSearch(e.target.value)}
                data-testid="reginv-drill-search" />
            </div>
            <Button variant="outline" size="sm" disabled={!filteredDrillRows.length}
              onClick={() => downloadBlob(
                `registration_${drill?.category || 'all'}_${drill?.branch || 'all'}.csv`,
                toCsv(filteredDrillRows, STUDENT_COLUMNS), 'text/csv;charset=utf-8;')}>
              <Download className="w-4 h-4 mr-2" />CSV
            </Button>
          </div>

          <div className="overflow-auto flex-1">
            {drillLoading ? (
              <div className="flex items-center justify-center h-40">
                <Loader2 className="w-6 h-6 animate-spin text-[#3E3F96]" />
              </div>
            ) : filteredDrillRows.length === 0 ? (
              <p className="text-sm text-[#5C635F] py-8 text-center">No students to show.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    {STUDENT_COLUMNS.map((c) => <TableHead key={c.label}>{c.label}</TableHead>)}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredDrillRows.map((r) => (
                    <TableRow key={r.id}>
                      {STUDENT_COLUMNS.map((c) => (
                        <TableCell key={c.label} className="text-sm">{c.get(r) || '—'}</TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RegistrationInvoicedReport;
